import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export function CombatTooltip({ name, description, note, className, focusable = true, children }: {
  name: string; description: string; note?: string; className?: string; focusable?: boolean
  children: (describedBy: string | undefined) => ReactNode
}) {
  const id = useId()
  const anchor = useRef<HTMLSpanElement>(null)
  const tip = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ left: 8, top: 8 })
  const show = () => { clearTimeout(timer.current); setOpen(true) }
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 120) }
  useEffect(() => () => clearTimeout(timer.current), [])
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    const outside = (e: PointerEvent) => { if (!anchor.current?.contains(e.target as Node) && !tip.current?.contains(e.target as Node)) close() }
    document.addEventListener('keydown', key)
    document.addEventListener('pointerdown', outside)
    window.addEventListener('resize', close)
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('pointerdown', outside); window.removeEventListener('resize', close) }
  }, [open])
  useLayoutEffect(() => {
    if (!open || !anchor.current || !tip.current) return
    const place = () => {
      const a = anchor.current!.getBoundingClientRect()
      const consoleRect = anchor.current!.closest('.console')?.getBoundingClientRect()
      // On a side-by-side layout, use the arena rather than covering the next input.
      const beside = consoleRect && consoleRect.left >= 216
      tip.current!.style.maxWidth = beside ? `${Math.min(320, consoleRect.left - 16)}px` : ''
      const t = tip.current!.getBoundingClientRect()
      if (beside) {
        setPosition({ left: consoleRect.left - t.width - 8, top: Math.max(8, Math.min(innerHeight - t.height - 8, a.top)) })
        return
      }
      const left = Math.max(8, Math.min(innerWidth - t.width - 8, a.x + a.width / 2 - t.width / 2))
      const consoleTop = consoleRect?.top ?? a.top
      const above = Math.min(a.top, consoleTop) - t.height - 8
      setPosition({ left, top: Math.max(8, Math.min(innerHeight - t.height - 8, above >= 8 ? above : a.bottom + 8)) })
    }
    place()
    window.addEventListener('scroll', place, true)
    return () => window.removeEventListener('scroll', place, true)
  }, [open, name, description, note])
  return <span ref={anchor} className={className} tabIndex={focusable ? 0 : undefined}
    aria-label={focusable ? `${name}: ${description}` : undefined} aria-describedby={open ? id : undefined}
    onPointerEnter={e => { if (e.pointerType !== 'touch') show() }} onPointerLeave={e => { if (e.pointerType !== 'touch') hide() }}
    onPointerDown={e => { if (e.pointerType === 'touch') show() }} onFocus={show} onBlur={hide} onClick={show}>
    {children(open ? id : undefined)}
    {open && createPortal(<div ref={tip} id={id} role="tooltip" className="combat-tooltip" style={position}
      onPointerEnter={show} onPointerLeave={hide}><strong>{name}</strong><span>{description}</span>{note && <small>{note}</small>}</div>, document.body)}
  </span>
}
